<template>
    <div class="autocomplete-textarea-wrapper" style="position: relative;">
        <b-form-textarea
            ref="textarea"
            v-bind="$attrs"
            :value="value"
            class="textarea-autosize"
            @input="onInput"
            @keydown.native="onKeydown"
            @blur="onBlur"
            @click.native="dismissPopup"
        />
        <div v-if="showPopup && filteredShortcodes.length" ref="popup" class="autocomplete-popup" :style="popupStyle">
            <div
                v-for="(item, index) in filteredShortcodes"
                :key="item.code"
                :class="{ 'autocomplete-item': true, 'active': index === selectedIndex }"
                @mousedown.prevent="selectItem(index)"
            >
                <div class="d-flex justify-content-between align-items-start">
                    <strong class="text-nowrap">/{{ item.code }}</strong>
                    <small v-if="item.category" class="text-muted ml-2">{{ item.category }}</small>
                </div>
                <small class="text-muted d-block text-truncate">{{ item.expansion }}</small>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import TextShortcodeService from '@/services/TextShortcodeService';
import { TextShortcode } from '@/models/TextShortcode';

const MIRROR_STYLE_PROPS = [
    'direction', 'boxSizing', 'width', 'overflowX',
    'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
    'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'fontStyle', 'fontVariant', 'fontWeight', 'fontStretch', 'fontSize',
    'fontSizeAdjust', 'lineHeight', 'fontFamily',
    'textAlign', 'textTransform', 'textIndent', 'textDecoration',
    'letterSpacing', 'wordSpacing', 'tabSize', 'whiteSpace', 'wordWrap',
] as const;

@Component({
    name: 'AutoCompleteTextarea',
    inheritAttrs: false
})
export default class AutoCompleteTextarea extends Vue {
    @Prop() private value!: string;

    private shortcodes: TextShortcode[] = [];
    private showPopup = false;
    private selectedIndex = 0;
    private searchTerm = '';
    private slashStart = -1;

    private popupStyle: Record<string, string> = {};

    async created() {
        await this.loadShortcodes();
    }

    @Watch('$root.$data.textShortcodesVersion')
    async onShortcodesVersionChanged() {
        TextShortcodeService.invalidateCache();
        await this.loadShortcodes();
    }

    async loadShortcodes() {
        try {
            this.shortcodes = await TextShortcodeService.getAll();
        } catch {
            // silently fail - autocomplete is non-critical
        }
    }

    get filteredShortcodes(): TextShortcode[] {
        if (!this.searchTerm) return this.shortcodes;
        const term = this.searchTerm.toLowerCase();
        const byCode = this.shortcodes.filter(s => s.code.toLowerCase().startsWith(term));
        if (byCode.length) return byCode;
        return this.shortcodes.filter(s => s.category?.toLowerCase().startsWith(term));
    }

    onInput(val: string) {
        this.$emit('input', val);
        this.checkForSlashCommand(val);
    }

    getCaretCoordinates(textarea: HTMLTextAreaElement, position: number, text?: string): { top: number; left: number } {
        const mirror = document.createElement('div');
        mirror.id = 'autocomplete-mirror';

        const computed = getComputedStyle(textarea);
        mirror.style.position = 'absolute';
        mirror.style.visibility = 'hidden';
        mirror.style.overflow = 'hidden';
        mirror.style.height = 'auto';

        for (const prop of MIRROR_STYLE_PROPS) {
            mirror.style[prop as any] = computed[prop as any];
        }

        mirror.style.whiteSpace = 'pre-wrap';
        mirror.style.wordWrap = 'break-word';

        const textBeforeCaret = (text ?? textarea.value).substring(0, position);
        mirror.textContent = textBeforeCaret;

        const marker = document.createElement('span');
        marker.textContent = '\u200b'; // zero-width space
        mirror.appendChild(marker);

        document.body.appendChild(mirror);

        const markerRect = marker.getBoundingClientRect();
        const mirrorRect = mirror.getBoundingClientRect();

        const top = markerRect.top - mirrorRect.top - textarea.scrollTop;
        const left = markerRect.left - mirrorRect.left;

        document.body.removeChild(mirror);

        return { top, left };
    }

    updatePopupPosition(currentValue?: string) {
        const textarea = (this.$refs.textarea as any)?.$el as HTMLTextAreaElement | undefined;
        if (!textarea) return;

        const coords = this.getCaretCoordinates(textarea, this.slashStart, currentValue);

        const wrapperRect = (this.$el as HTMLElement).getBoundingClientRect();
        const textareaRect = textarea.getBoundingClientRect();
        const textareaOffsetTop = textareaRect.top - wrapperRect.top;
        const textareaOffsetLeft = textareaRect.left - wrapperRect.left;

        const popupLeft = textareaOffsetLeft + coords.left;
        const popupTop = textareaOffsetTop + coords.top;

        this.popupStyle = {
            position: 'absolute',
            bottom: `${(this.$el as HTMLElement).offsetHeight - popupTop}px`,
            left: `${popupLeft}px`,
            zIndex: '1050',
            maxHeight: '200px',
            overflowY: 'auto',
            minWidth: '280px',
            maxWidth: '400px',
        };
    }

    checkForSlashCommand(val: string) {
        const textarea = (this.$refs.textarea as any)?.$el as HTMLTextAreaElement | undefined;
        if (!textarea) return;

        const cursorPos = textarea.selectionStart;
        const textBeforeCursor = val.substring(0, cursorPos);

        // Find the last `/` that starts a word (preceded by start-of-string, whitespace, or newline)
        const match = textBeforeCursor.match(/(?:^|[\s\n])\/([\w]*)$/);
        if (match) {
            this.slashStart = cursorPos - match[1].length - 1; // position of the `/`
            this.searchTerm = match[1];
            this.selectedIndex = 0;
            this.updatePopupPosition(val);
            this.showPopup = true;
        } else {
            this.dismissPopup();
        }
    }

    onKeydown(e: KeyboardEvent) {
        if (!this.showPopup || !this.filteredShortcodes.length) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                this.selectedIndex = (this.selectedIndex + 1) % this.filteredShortcodes.length;
                this.scrollToSelected();
                break;
            case 'ArrowUp':
                e.preventDefault();
                this.selectedIndex = (this.selectedIndex - 1 + this.filteredShortcodes.length) % this.filteredShortcodes.length;
                this.scrollToSelected();
                break;
            case 'Enter':
            case 'Tab':
                e.preventDefault();
                this.selectItem(this.selectedIndex);
                break;
            case 'Escape':
                e.preventDefault();
                this.dismissPopup();
                break;
        }
    }

    selectItem(index: number) {
        const item = this.filteredShortcodes[index];
        if (!item) return;

        const textarea = (this.$refs.textarea as any)?.$el as HTMLTextAreaElement | undefined;
        if (!textarea) return;

        const cursorPos = textarea.selectionStart;
        const val = this.value || '';
        const before = val.substring(0, this.slashStart);
        const after = val.substring(cursorPos);
        const newValue = before + item.expansion + after;

        this.$emit('input', newValue);
        this.dismissPopup();

        this.$nextTick(() => {
            const newPos = before.length + item.expansion.length;
            textarea.setSelectionRange(newPos, newPos);
            textarea.focus();
        });
    }

    onBlur() {
        // Delay to allow mousedown on popup items to fire first
        setTimeout(() => {
            this.dismissPopup();
        }, 200);
    }

    scrollToSelected() {
        this.$nextTick(() => {
            const popup = this.$refs.popup as HTMLElement | undefined;
            if (!popup) return;
            const active = popup.querySelector('.autocomplete-item.active') as HTMLElement | null;
            if (active) {
                active.scrollIntoView({ block: 'nearest' });
            }
        });
    }

    dismissPopup() {
        this.showPopup = false;
        this.selectedIndex = 0;
        this.searchTerm = '';
        this.slashStart = -1;
    }
}
</script>

<style scoped>
.autocomplete-popup {
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.autocomplete-item {
    padding: 6px 10px;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
}

.autocomplete-item:last-child {
    border-bottom: none;
}

.autocomplete-item:hover,
.autocomplete-item.active {
    background-color: #e9ecef;
}
</style>
